import { faCompressWide, faExpand } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@kcftech/design-system';
import { ICorporationResponse } from '@kcftech/prometheus-client';
import { useFullscreen } from 'custom_hooks/useFullscreen';
import { memo, ReactNode } from 'react';
import styled from 'styled-components';

import { CorporationSwitcherDropdown } from './CorporationSwitcherDropdown';
import { DashboardId, DashboardSwitcherDropdown } from './DashboardSwitcherDropdown';
import { Header, HeaderControls, QuickFilterHeaderRegion } from './styled';

export type DashboardHeaderProps = Readonly<{
    /** Corporation object containing logo and name */
    corporation: ICorporationResponse;
    /** Current dashboard ID to show in the switcher dropdown (optional) */
    currentDashboard?: DashboardId;
    /** Filter buttons to render in the header controls */
    children: ReactNode;
}>;

const Title = styled.h1`
    margin: 0;
    display: inline-flex;
    align-items: center;
    gap: 1rem;
`;

const FullscreenButton = memo(function FullscreenButtonComponent() {
    const { isFullscreen, toggleFullscreen } = useFullscreen({ hideSidebar: true });

    return (
        <Button
            appearance="subtle"
            spacing="compact"
            onClick={toggleFullscreen}
            aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
        >
            <FontAwesomeIcon icon={isFullscreen ? faCompressWide : faExpand} />
        </Button>
    );
});

/**
 * Reusable header component for dashboard pages
 *
 * Displays:
 * - Corporation logo
 * - Corporation name / dropdown
 * - Optional dashboard switcher dropdown (when currentDashboard is provided)
 * - Filter buttons in QuickFilterHeaderRegion
 *
 * @example
 * ```tsx
 * <DashboardHeader
 *     corporation={corporation}
 *     currentDashboard="desk-activity-overview"
 * >
 *     <SeverityFilterButton {...props} />
 *     <StatusFilterButton {...props} />
 * </DashboardHeader>
 * ```
 */
export const DashboardHeader = memo(function DashboardHeader({
    corporation,
    currentDashboard,
    children,
}: DashboardHeaderProps) {
    return (
        <Header className="head">
            <Title>
                <CorporationSwitcherDropdown corporation={corporation} />
                {currentDashboard && (
                    <DashboardSwitcherDropdown
                        corporationId={corporation.Id}
                        currentDashboard={currentDashboard}
                    />
                )}
            </Title>

            <HeaderControls>
                <QuickFilterHeaderRegion>{children}</QuickFilterHeaderRegion>
                <FullscreenButton />
            </HeaderControls>
        </Header>
    );
});
